import { useTranslation } from 'react-i18next';

import ENS from '@/assets/ENS.jpg';
import FacebookEmBlueIcon from '@/assets/facebook-icon-emBlue.svg';
import FacebookIcon from '@/assets/facebook-icon.svg';
import LetterIcon from '@/assets/letter-icon.svg';
import LinkedInEmBlueIcon from '@/assets/linkedin-icon-emBlue.svg';
import LinkedInIcon from '@/assets/linkedin-icon.svg';
import PhoneIcon from '@/assets/phone-icon.svg';
import YouTubeEmBlueIcon from '@/assets/youtube-icon-emBlue.svg';
import YouTubeIcon from '@/assets/youtube-icon.svg';
import { ADMIN_EMAIL_ADDRESS, APP_HOME_URL, DEFAULT_THEME } from '@/config';
import { useGeoLocation } from '@/hooks/useGeoLocation';
import { themes } from '@/utils/helpers';

export const PublicFooter = () => {
  const { t } = useTranslation();
  const { isSet, isEurope } = useGeoLocation();

  const titleLinkStyle = () =>
    'text-xs uppercase text-md-purple font-bold mb-3 hover:text-md-purple hover:no-underline hover:opacity-80';
  const linkStyle = () => 'my-0 lg:my-1 block text-black text-sm uppercase';
  const addressTitleStyle = () => 'text-base text-md-blue font-black mb-4 flex gap-2';
  const addressStyle = () => 'my-1 block text-md-blue text-[13px] font-normal font-secondary';
  const legalLinkStyle = () =>
    'font-black uppercase text-md-blue text-xs w-1/2 lg:w-auto text-center lg:text-right py-2 lg:py-0';
  const getLogoUrl = () =>
    themes[DEFAULT_THEME].assetsURL + 'globals/src/images/logos/logo_MDirector.png';
  return (
    <footer className="bg-white">
      <div className="max-w-6xl m-auto text-black flex flex-wrap justify-center lg:justify-between mt-10 mb-2 items-center border-b-2 border-md-bg-gray pb-8">
        <a
          href={APP_HOME_URL}
          title="MDirector"
          className="pb-6 lg:pb-0 border-b-2 border-md-lightGray lg:border-0 mb-4 lg:mb-0 w-full lg:w-auto"
        >
          <img
            className="pl-0 lg:pl-4 m-auto w-60 lg:w-[218px]"
            width="218"
            alt="MDirector logo"
            src={`${getLogoUrl()}`}
          />
        </a>
        {isSet && isEurope() && (
          <a
            href={`mailto:${ADMIN_EMAIL_ADDRESS}`}
            title={t('PublicFooter.emailUs')}
            className="flex items-center gap-2 w-full lg:w-auto flex justify-center mb-4 lg:mb-0 hover:opacity-80 hover:no-underline"
          >
            <img src={LetterIcon} alt={t('PublicFooter.emailUs')} width="50" height="50" />
            <span className="text-black font-bold font-secondary text-base">
              {ADMIN_EMAIL_ADDRESS}
            </span>
          </a>
        )}
        {isSet && isEurope() && (
          <a
            href={`tel:${t('PublicFooter.phoneNumberRaw')}`}
            title={t('PublicFooter.phoneUs')}
            className="flex items-center gap-2 w-full lg:w-auto flex justify-center mb-6 lg:mb-0 hover:opacity-80 hover:no-underline"
          >
            <img src={PhoneIcon} alt={t('PublicFooter.phoneUs')} width="50" height="50" />
            <span className="text-black font-bold font-secondary text-base">
              {t('PublicFooter.phoneNumber')}
            </span>
          </a>
        )}
        <div className="flex items-center gap-5">
          <span className="text-black font-black uppercase min-w-[120px]">
            {t('PublicFooter.followUs')}
          </span>
          <div className="flex gap-2">
            <a
              href={t('PublicFooter.SocialNetworks.YouTubeURL')}
              target="_blank"
              title="Youtube"
              rel="noreferrer"
            >
              <img
                className="w-10 lg:w-auto"
                src={isSet && isEurope() ? YouTubeIcon : YouTubeEmBlueIcon}
                alt="YouTube"
                width="50"
                height="50"
              />
            </a>
            <a
              href={t('PublicFooter.SocialNetworks.LinkedInURL')}
              target="_blank"
              title="LinkedIn"
              rel="noreferrer"
            >
              <img
                className="w-10 lg:w-auto"
                src={isSet && isEurope() ? LinkedInIcon : LinkedInEmBlueIcon}
                alt="LinkedIn"
                width="50"
                height="50"
              />
            </a>
            <a
              href={t('PublicFooter.SocialNetworks.FacebookURL')}
              target="_blank"
              title="Facebook"
              rel="noreferrer"
            >
              <img
                className="w-10 lg:w-auto"
                src={isSet && isEurope() ? FacebookIcon : FacebookEmBlueIcon}
                alt="Facebook"
                width="50"
                height="50"
              />
            </a>
          </div>
        </div>
      </div>

      {isSet && isEurope() && (
        <div className="max-w-6xl m-auto text-black flex flex-wrap justify-left md:border-solid md:border-t text-center lg:text-left lg:justify-between border-t-2 border-md-lightGray lg:border-0 pt-6 lg:pt-0 mt-8">
          <div className="p-5 w-full lg:w-1/5">
            <a
              className={titleLinkStyle()}
              title={t('PublicFooter.Solutions.title')}
              href={t('PublicFooter.Solutions.titleURL')}
            >
              {t('PublicFooter.Solutions.title')}
            </a>
            <ol className="flex lg:block flex-wrap justify-center gap-5">
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Solutions.emailMarketingURL')}
                  title={t('PublicFooter.Solutions.emailMarketing')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Solutions.emailMarketing')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Solutions.smsMarketingURL')}
                  title={t('PublicFooter.Solutions.smsMarketing')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Solutions.smsMarketing')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Solutions.landingOptimizerURL')}
                  title={t('PublicFooter.Solutions.landingOptimizer')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Solutions.landingOptimizer')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Solutions.marketingAutomationURL')}
                  title={t('PublicFooter.Solutions.marketingAutomation')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Solutions.marketingAutomation')}
                </a>
              </li>
            </ol>
          </div>

          <div className="p-5 w-full lg:w-1/5">
            <a
              className={titleLinkStyle()}
              title={t('PublicFooter.Plans.title')}
              href={t('PublicFooter.Plans.titleURL')}
            >
              {t('PublicFooter.Plans.title')}
            </a>
            <ol className="flex lg:block flex-wrap justify-center gap-5">
              <li className="leading-3">
                <a href={t('PublicFooter.Plans.pricesURL')} className={linkStyle()}>
                  {t('PublicFooter.Plans.prices')}
                </a>
              </li>
            </ol>
          </div>

          <div className="p-5 w-full lg:w-1/5">
            <a
              className={titleLinkStyle()}
              title={t('PublicFooter.Support.title')}
              href={t('PublicFooter.Support.titleURL')}
            >
              {t('PublicFooter.Support.title')}
            </a>
            <ol className="flex lg:block flex-wrap justify-center gap-5">
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Support.supportURL')}
                  title={t('PublicFooter.Support.support')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Support.support')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Support.contactURL')}
                  title={t('PublicFooter.Support.contact')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Support.contact')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Support.resourcesURL')}
                  title={t('PublicFooter.Support.resources')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Support.resources')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Support.blogURL')}
                  title={t('PublicFooter.Support.blog')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Support.blog')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.Support.APIURL')}
                  title={t('PublicFooter.Support.API')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.Support.API')}
                </a>
              </li>
            </ol>
          </div>

          <div className="p-5 w-full lg:w-1/5">
            <a
              className={titleLinkStyle()}
              title={t('PublicFooter.About.title')}
              href={t('PublicFooter.About.titleURL')}
            >
              {t('PublicFooter.About.title')}
            </a>
            <ol className="flex lg:block flex-wrap justify-center gap-5">
              <li className="leading-3">
                <a
                  href={t('PublicFooter.About.companyURL')}
                  title={t('PublicFooter.About.company')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.About.company')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.About.delegationsURL')}
                  title={t('PublicFooter.About.delegations')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.About.delegations')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.About.certificationsURL')}
                  title={t('PublicFooter.About.certifications')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.About.certifications')}
                </a>
              </li>
              <li className="leading-3">
                <a
                  href={t('PublicFooter.About.talentURL')}
                  title={t('PublicFooter.About.talent')}
                  className={linkStyle()}
                >
                  {t('PublicFooter.About.talent')}
                </a>
              </li>
            </ol>
          </div>

          <div className="p-5 lg:pr-0 w-full lg:w-1/5">
            <img className="m-auto lg:ml-auto lg:mr-0" src={ENS} alt="ENS" />
          </div>
        </div>
      )}

      {isSet && isEurope() && (
        <div className="bg-md-bg-gray max-w-6xl m-auto text-black flex-wrap justify-left hidden lg:flex text-center lg:text-left rounded-lg">
          <div className="p-5 w-1/2 lg:w-1/6">
            <div className={addressTitleStyle()}>
              <img
                className="w-4 h-5"
                alt={t('PublicFooter.Addresses.location')}
                src="https://cdn-blog.mdirector.com/wp-content/uploads/2022/02/location.png"
                width="16"
                height="20"
              />
              {t('PublicFooter.Addresses.Spain.title')}
            </div>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Spain.address_1')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Spain.address_2')}</p>
          </div>
          <div className="p-5 w-1/2 lg:w-1/6">
            <div className={addressTitleStyle()}>
              <img
                className="w-4 h-5"
                alt={t('PublicFooter.Addresses.location')}
                src="https://cdn-blog.mdirector.com/wp-content/uploads/2022/02/location.png"
                width="16"
                height="20"
              />
              {t('PublicFooter.Addresses.Italy.title')}
            </div>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Italy.address_1')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Italy.address_2')}</p>
          </div>
          <div className="p-5 w-1/2 lg:w-1/6">
            <div className={addressTitleStyle()}>
              <img
                className="w-4 h-5"
                alt={t('PublicFooter.Addresses.location')}
                src="https://cdn-blog.mdirector.com/wp-content/uploads/2022/02/location.png"
                width="16"
                height="20"
              />
              {t('PublicFooter.Addresses.Mexico.title')}
            </div>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Mexico.address_1')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Mexico.address_2')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Mexico.address_3')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Mexico.address_4')}</p>
          </div>
          <div className="p-5 w-1/2 lg:w-1/6">
            <div className={addressTitleStyle()}>
              <img
                className="w-4 h-5"
                alt={t('PublicFooter.Addresses.location')}
                src="https://cdn-blog.mdirector.com/wp-content/uploads/2022/02/location.png"
                width="16"
                height="20"
              />
              {t('PublicFooter.Addresses.Colombia.title')}
            </div>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Colombia.address_1')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Colombia.address_2')}</p>
          </div>
          <div className="p-5 w-1/2 lg:w-1/6">
            <div className={addressTitleStyle()}>
              <img
                className="w-4 h-5"
                alt={t('PublicFooter.Addresses.location')}
                src="https://cdn-blog.mdirector.com/wp-content/uploads/2022/02/location.png"
                width="16"
                height="20"
              />
              {t('PublicFooter.Addresses.Chile.title')}
            </div>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Chile.address_1')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Chile.address_2')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Chile.address_3')}</p>
          </div>
          <div className="p-5 w-1/2 lg:w-1/6">
            <div className={addressTitleStyle()}>
              <img
                className="w-4 h-5"
                alt={t('PublicFooter.Addresses.location')}
                src="https://cdn-blog.mdirector.com/wp-content/uploads/2022/02/location.png"
                width="16"
                height="20"
              />
              {t('PublicFooter.Addresses.Argentina.title')}
            </div>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Argentina.address_1')}</p>
            <p className={addressStyle()}>{t('PublicFooter.Addresses.Argentina.address_2')}</p>
          </div>
        </div>
      )}

      {isSet && isEurope() && (
        <div className="max-w-6xl m-auto text-black flex flex-wrap justify-left border-t-2 border-md-lightGray lg:border-0 mt-3 lg:mt-0">
          <div className="flex pb-5 m-auto pt-3 text-gray-800 text-xs flex-col md:flex-row justify-between w-full">
            <div className="mt-2 lg:w-6/12 text-center lg:text-left mb-4 lg:mb-0 font-secondary">
              {t('PublicFooter.copyright')}
              <br />
              {t('PublicFooter.certificated')}
            </div>
            <div className="mt-2 flex flex-row justify-end flex-wrap lg:flex-auto lg:w-6/12 lg:flex-nowrap lg:gap-8">
              <a href={t('LegalTerms.privacyPolicyURL')} className={legalLinkStyle()}>
                {t('LegalTerms.privacyPolicy')}
              </a>
              <a href={t('LegalTerms.termsURL')} className={legalLinkStyle()}>
                {t('LegalTerms.terms')}
              </a>
              <a href={t('LegalTerms.cookiesPolicyURL')} className={legalLinkStyle()}>
                {t('LegalTerms.cookiesPolicy')}
              </a>
              <a href={t('PublicFooter.Sitemap.url')} className={legalLinkStyle()}>
                {t('PublicFooter.Sitemap.title')}
              </a>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
